// Constants and Basic Types
import { starsToken } from "./token"
import type { CurrencyType, PriceOption, PriceSetter } from "./customsList"

export type BoostType = "cardHint" | "swapCard" | "cancelMove"

export interface BoostItem {
  readonly type: BoostType
  prices: PriceOption[]
  readonly enabled: boolean
}

// Extended item interface that includes custom prices
export interface BoostItemInput {
  type: BoostType
  prices?: PriceOption[]
}

// Enhanced BoostItem with price setting capabilities
export interface MutableBoostItem extends BoostItem, PriceSetter { }

// Simplified price configurations
const priceConfigs = {
  free: [{ currency: "balance", price: 0 }] as const,
  balanceOnly(price: number) {
    return [{ currency: "balance", price }] as const
  },
  starsOnly(price: number) {
    return [{ currency: starsToken, price }] as const
  },
  both(balancePrice: number, starsPrice: number) {
    return [
      { currency: starsToken, price: starsPrice },
      { currency: "balance", price: balancePrice },
    ] as const
  },
}

// Default boost prices
const defaultBoostPrices: Record<BoostType, PriceOption[]> = {
  cardHint: [...priceConfigs.balanceOnly(200)],
  swapCard: [...priceConfigs.starsOnly(5)],
  cancelMove: [...priceConfigs.starsOnly(5)],
}

// Helper function to merge custom prices with default prices
function mergePrices(
  defaultPrices: PriceOption[],
  customPrices?: PriceOption[]
): PriceOption[] {
  if (!customPrices) return [...defaultPrices]

  if (customPrices.find((p) => p.price === 0)) return [...customPrices]

  const mergedPrices = [...defaultPrices]

  customPrices.forEach((customPrice) => {
    const existingIndex = mergedPrices.findIndex(
      (p) => p.currency === customPrice.currency
    )
    if (existingIndex >= 0) {
      mergedPrices[existingIndex] = customPrice
    } else {
      mergedPrices.push(customPrice)
    }
  })

  return mergedPrices
}

// Enhanced helper function for creating boosts with price setting capabilities
function createBoosts(items: BoostItemInput[]): MutableBoostItem[] {
  return items.map((item) => {
    const defaultPrices = defaultBoostPrices[item.type]
    const prices = mergePrices(defaultPrices, item.prices)

    const mutableItem: MutableBoostItem = {
      ...item,
      prices,
      enabled: true,

      // Implement price setting methods
      setPrice(currency: CurrencyType, newPrice: number) {
        const priceIndex = this.prices.findIndex((p) => p.currency === currency)
        if (priceIndex >= 0) {
          this.prices[priceIndex] = { currency, price: newPrice }
        } else {
          this.prices.push({ currency, price: newPrice })
        }
      },

      setPrices(newPrices: PriceOption[]) {
        this.prices = [...newPrices]
      },
    }

    return mutableItem
  })
}

// Boost definitions with custom prices support
const boosts = createBoosts([
  {
    type: "cardHint",
    prices: [...priceConfigs.balanceOnly(200)],
  },
  {
    type: "swapCard",
    prices: [...priceConfigs.starsOnly(5)],
  },
  {
    type: "cancelMove",
    prices: [...priceConfigs.starsOnly(5)],
  },
])

// Export the boosts list
export const boostsList = [...boosts]

// Helper function to find boosts
export function findBoost(type: BoostType): MutableBoostItem | undefined {
  return boostsList.find((boost) => boost.type === type)
}

// Export for easy access to boost types
export const BOOST_TYPES: BoostType[] = ["cardHint", "swapCard", "cancelMove"] 